import styles from "../RemoteConsentForm/styles.module.css";
import Card from "../Card";
import { useEffect, useMemo, useState } from "react";
import useProfile from "../../hooks/useProfile";
import PersonalInfoForm from "../forms/PersonalInfoForm";
import { setLoader } from "../../features/LoaderSlice";
import { useDispatch } from "react-redux";
import useCustomRouting from "../../hooks/useCustomRouting";
import { FormattedMessage, useIntl } from "react-intl";
import useCollect from "../../hooks/useCollect";
import { VAULT, VAULT_ID } from "../../utils/constants";
import CocoLanguageForm from "../forms/CocoLanguageForm";
import { getSourceSystemId } from "../../utils/utils";
import MessageBox from "../MessageBox";
import PropTypes from "prop-types";

const CocoProfileUpdateForm = ({ values, versionInfo }) => {
  const intl = useIntl();
  const { tokenData } = useProfile();
  const dispatch = useDispatch();
  const { redirectToPath } = useCustomRouting();
  const {
    identityCollect,
    handleCollectSuccessMessage,
    handleCollectErrorMessage,
  } = useCollect();
  const [haveRequiredValues, setHaveRequiredValues] = useState(false);
  const [formData, setFormData] = useState({
    first_name: values.FirstName,
    last_name: values.LastName,
    email1: values.EmailInput1 ? decodeURIComponent(values.EmailInput1) : "",
    email2: values.EmailInput2 ? decodeURIComponent(values.EmailInput2) : "",
    default_email1: values.EmailInput1 ? true : false,
    mobile: values.PhoneInput,
    controller_code:
      values?.CountryCode?.toLowerCase() +
      "_" +
      values?.Division?.toLowerCase(),
    customer_scope: "hcp",
    type: getSourceSystemId(values?.SourceSystem),
    value: values.CustomerID ? decodeURIComponent(values.CustomerID) : "",
    language: values.Language,
    contact_email: values.EmailInput1 ? values.EmailInput1 : "",
    title: values.AcademicTitle,
    salutation: values.Salutation,
    bayer_id: values?.BayerID,
    external_provider_id: values?.externalProviderID,
  });
  const handleChange = (e) => {
    if (["default_email1", "default_email2"]?.includes(e.target.name)) {
      const isMail1 = e.target.name == "default_email1";
      setFormData((prev) => ({
        ...prev,
        default_email1: !!isMail1,
        default_email2: !isMail1,
        hasSubmitted: false,
      }));
    } else
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
        hasSubmitted: false,
      }));
  };
  const handleCollectCall = (payload) => {
    dispatch(setLoader({ loading: true, label: "" }));
    identityCollect(payload, true, false, {}, true, true, false)
      .then((res) => {
        handleCollectSuccessMessage(res);
        dispatch(setLoader({ loading: false, label: "" }));
        const urlParams = new URLSearchParams(window.location.search);
        redirectToPath(`/coco/?${urlParams}`);
      })
      .catch((error) => {
        handleCollectErrorMessage(error);
        dispatch(setLoader({ loading: false, label: "" }));
      });
  };
  const handleSubmitCollect = () => {
    setFormData((prev) => ({ ...prev, hasSubmitted: true }));
    if (!haveRequiredValues) return;
    dispatch(setLoader({ loading: true, label: "Saving..." }));
    const source = [];
    const sub_identities = [];
    const aliases = [];
    const processEmail = (email, emailType, field) => {
      if (!email) return;
      const isPersonal = emailType === "Account.PersonEmail";
      source.push({ field, value: email });

      const identity = { value: email, type: "email" };
      if (isPersonal) {
        sub_identities.push(identity);
      } else {
        aliases.push(identity);
      }
    };
    processEmail(formData.email1, formData.email_type1, "Account.PersonEmail");
    processEmail(formData.email2, formData.email_type2, "BPH_Email_2__c");
    const payload = {
      controller_code: formData.controller_code,
      customer_scope: formData.customer_scope,
      type: formData.type,
      value: formData.value,
      language: formData.language,
      title: formData.title,
      salutation: formData.salutation,
      last_name: formData.last_name,
      first_name: formData.first_name,
      loggedin_email: tokenData?.upn,
      contact_email: formData?.default_email1
        ? formData.email1
        : formData.email2,
      collection_channel: "Truman UI",
      last_update_channel: "Truman UI",
      external_provider_id: formData?.external_provider_id,
    };
    if (formData?.type === "veeva" && source.length) {
      payload.source_fields = { veeva: source };
    } else if (formData?.type === VAULT_ID && source.length) {
      payload.source_fields = {
        [VAULT]: source.map((fields) => {
          if (fields.field === "Account.PersonEmail") {
            return { ...fields, field: "account__v.email_cda__v" };
          } else if (fields.field === "BPH_Email_2__c") {
            return { ...fields, field: "account__v.bph_email_2__c" };
          }
          return fields;
        }),
      };
    }
    if (aliases.length) payload.aliases = aliases;
    if (sub_identities.length) payload.sub_identities = sub_identities;
    if (formData?.bayer_id) payload.bayer_id = formData.bayer_id;

    handleCollectCall({ ...payload });
  };
  const [emailTypes, setEmailTypes] = useState([]);
  useEffect(() => {
    if (typeof versionInfo === "object" && Object.keys(versionInfo).length) {
      const personal = {
        value: "Account.PersonEmail",
        label: intl.formatMessage({
          id: "label.personal",
          defaultMessage: "Personal",
        }),
      };
      const shared = {
        value: "BPH_Email_2__c",
        label: intl.formatMessage({
          id: "label.shared",
          defaultMessage: "Shared",
        }),
      };
      const types = [];
      if (versionInfo?.emailTypes) {
        if (versionInfo?.emailTypes?.includes("personal")) types.push(personal);
        if (versionInfo?.emailTypes?.includes("shared")) types.push(shared);
      } else {
        types.push(personal, shared);
      }
      setEmailTypes(types);
    }
  }, [versionInfo]);
  useEffect(() => {
    if (emailTypes?.length) {
      const emailTypeValue = emailTypes[0]?.value;

      setFormData((prev) => ({
        ...prev,
        ...(formData?.email1 &&
          !formData?.email_type1 && { email_type1: emailTypeValue }),
        ...(formData?.email2 &&
          !formData?.email_type2 && { email_type2: emailTypeValue }),
      }));
    }
  }, [emailTypes]);
  const getMinRequiredFields = () => {
    if (Number(versionInfo?.requiredAnyFields)) {
      return Number(versionInfo?.requiredAnyFields);
    }
    if (versionInfo?.requiredFields) {
      return versionInfo?.requiredFields.split(",").length;
    }
    if (values?.EmailInput1 || values?.EmailInput2) {
      return 1;
    }
    return 0;
  };
  const addEmailValidations = (validation, emailField, emailTypeField) => {
    validation[emailField] = {
      required: true,
      message: versionInfo?.requiredValidationError,
    };
    validation[emailTypeField] = {
      required: true,
      message: versionInfo?.requiredValidationError,
    };
  };

  const getValidationError = () => {
    const validation = {
      first_name: {
        required: true,
        message: versionInfo?.requiredValidationError,
      },
      last_name: {
        required: true,
        message: versionInfo?.requiredValidationError,
      },
    };

    const minVal = getMinRequiredFields();
    if (minVal > 0) {
      const requiredAnyFields = Number(versionInfo?.requiredAnyFields);

      const isEmail1Required = versionInfo?.requiredFields?.includes(
        "Account.PersonEmail",
      );
      const isEmail2Required =
        versionInfo?.requiredFields?.includes("BPH_Email_2__c");
      if ((!requiredAnyFields && isEmail1Required) || values?.EmailInput1) {
        addEmailValidations(validation, "email1", "email_type1");
      }
      if ((!requiredAnyFields && isEmail2Required) || values?.EmailInput2) {
        addEmailValidations(validation, "email2", "email_type2");
      }
    }
    return validation;
  };
  const isEmail1Required = useMemo(
    () => versionInfo?.requiredFields?.includes("Account.PersonEmail"),
    [versionInfo],
  );
  const isEmail2Required = useMemo(
    () => versionInfo?.requiredFields?.includes("BPH_Email_2__c"),
    [versionInfo],
  );
  useEffect(() => {
    const isValid = () => {
      if (!areNamesValid()) return false;

      const minVal = getMinRequiredFields();
      if (minVal && !areEmailsValid(minVal)) return false;


      return true;
    };

    const areNamesValid = () => {
      return formData?.first_name && formData?.last_name;
    };

    const areEmailsValid = (minVal) => {
      if (minVal > 1) {
        return formData?.email1 && formData?.email2;
      }
      if (
        versionInfo?.requiredFields &&
        versionInfo?.requiredFields?.split(",")?.length == 1
      )
        return (
          (isEmail1Required && formData?.email1) ||
          (isEmail2Required && formData?.email2)
        );
      else return formData?.email1 || formData?.email2;
    };

    setHaveRequiredValues(isValid());
  }, [formData, versionInfo]);

  const hasEmail = useMemo(() => {
    if (getMinRequiredFields() > 0)
      return values?.EmailInput1 || values?.EmailInput2;
    else return true;
  }, [versionInfo, values]);

  return (
    <div className={styles["form-container"]}>
      <h1 className={styles["consent-title"]}>
        <FormattedMessage
          id="coco.option.profile-update"
          defaultMessage="Profile Update"
        />
      </h1>
      {!hasEmail && (
        <div className={styles["container-no-email-validation"]}>
          <MessageBox
            label={intl.formatMessage({
              id: "coco.profile-update.no-email-validation",
              defaultMessage:
                "For Profile Update, an email address is required. Please add an email address in the CRM system and try again.",
            })}
            state="error"
          />
        </div>
      )}
      <Card className={styles["form-card"]}>
        <p className={styles["form-title"]}>
          <FormattedMessage
            id="coco.rc-form.personal-data"
            defaultMessage="Personal Data"
          />
        </p>
        <PersonalInfoForm
          formData={formData}
          handleChange={handleChange}
          queryValues={values}
          emailTypes={emailTypes}
          versionInfo={versionInfo}
          getValidationError={getValidationError}
        />
      </Card>
      <Card className={styles["form-card"]}>
        <p className={styles["form-title"]}>
          <FormattedMessage id="label.consent" defaultMessage="Consent" />
        </p>
        <CocoLanguageForm
          formData={formData}
          handleChange={handleChange}
          versionInfo={versionInfo}
          setFormData={setFormData}
        />
      </Card>
      <button
        className={`btn-primary ${styles["submit-button"]}`}
        onClick={handleSubmitCollect}
        disabled={formData?.language === ""}
      >
        <FormattedMessage
          id="button.save-profile"
          defaultMessage="Save Profile"
        />
      </button>
    </div>
  );
};
export default CocoProfileUpdateForm;

CocoProfileUpdateForm.propTypes = {
  values: PropTypes.shape({
    FirstName: PropTypes.string,
    LastName: PropTypes.string,
    EmailInput1: PropTypes.string,
    EmailInput2: PropTypes.string,
    PhoneInput: PropTypes.string,
    CountryCode: PropTypes.string,
    Division: PropTypes.string,
    SourceSystem: PropTypes.string,
    CustomerID: PropTypes.string,
    Language: PropTypes.string,
    AcademicTitle: PropTypes.string,
    Salutation: PropTypes.string,
    BayerID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    externalProviderID: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  versionInfo: PropTypes.object,
};

CocoProfileUpdateForm.defaultProps = {
  versionInfo: {},
};
